import './license-list.scss';

import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { AddLicenseModal } from './license-modal';
import { ApolloError } from '@apollo/client';
import {
  DeleteBrokerageLicense,
  LicensePaginationSort,
  LicenseSortKey,
  SortOrder,
} from '@converge/graphql';
import { DeleteLicenseModal } from '../entity/broker-agencies/broker-form/delete-license-modal';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { ILicenses } from './accordion.form-container';
import { LicenseModal } from './message-license-modal';
import { NewDisplayFormatDate } from '@converge/utils';
import { config } from '@converge/config';
import { useConvergeSnackBar } from '../../contexts';
import { useState, useEffect } from 'react';

export interface ILicenseListProps {
  handleSubmitFromChild: () => void;

  brokerageUuid: string | undefined;
  brokerageLicense?: ILicenses[];
  onSortChange?: (sortModel: LicensePaginationSort) => void;
  currentSort?: LicensePaginationSort;
  isLoading?: boolean;
}
export interface ISelectedDeleteLicense {
  licenseUuid: string | null | undefined;
  brokerageUuid: string | null | undefined;
}

const LicenseList = ({
  handleSubmitFromChild,
  brokerageUuid,
  brokerageLicense,
  onSortChange,
  currentSort,
  isLoading = false,
}: ILicenseListProps) => {
  const renderTableCell = (value: string, testId?: string) => (
    <Typography
      className="table-cell"
      data-testid={testId}
      sx={{
        width: '100%',
        paddingLeft: '100px',
      }}
    >
      {value || '—'}
    </Typography>
  );

  const renderSkeletonCell = (testId?: string) => (
    <Box
      className="table-cell"
      data-testid={'skeleton-cell-' + testId}
      sx={{
        width: '100%',
        paddingLeft: '20px',
        paddingRight: '20px',
      }}
    >
      <Skeleton animation="wave" width="80%" height={20} />
    </Box>
  );
  const { notify } = useConvergeSnackBar();
  const [isBrokerageSave, setIsBrokerageSave] = useState(false);

  // Local storage utilities for license sort
  const STORAGE_KEY = 'brokerage-license-sort';

  const getStoredSort = (): LicensePaginationSort => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to parse stored license sort:', error);
    }
    // Default sort by created_at desc
    return {
      key: LicenseSortKey.CreatedAt,
      order: SortOrder.Desc,
    };
  };

  const setStoredSort = (sort: LicensePaginationSort) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sort));
    } catch (error) {
      console.warn('Failed to store license sort:', error);
    }
  };

  // Initialize sort from storage or use current sort
  const [sortModel, setSortModel] = useState<GridSortModel>(() => {
    const sort = currentSort || getStoredSort();

    // Map GraphQL sort to DataGrid sort model
    const fieldMap: Record<LicenseSortKey | string, string> = {
      [LicenseSortKey.Type]: 'licenseType',
      [LicenseSortKey.Region]: 'state',
      [LicenseSortKey.Number]: 'licenseNumber',
      [LicenseSortKey.ExpirationDate]: 'expirationDate',
      [LicenseSortKey.Class]: 'licenseType', // Map class to type
      [LicenseSortKey.CreatedAt]: 'createdAt',
    };

    if (sort.key === LicenseSortKey.CreatedAt) {
      return [];
    }

    const field = sort.key
      ? fieldMap[sort.key] || 'licenseType'
      : 'licenseType';
    const sortOrder = sort.order === SortOrder.Desc ? 'desc' : 'asc';

    return [{ field, sort: sortOrder }];
  });

  // Effect to handle initial sort setup
  useEffect(() => {
    if (!currentSort) {
      const storedSort = getStoredSort();
      onSortChange?.(storedSort);
    }
  }, [currentSort, onSortChange]);
  const [addNewLicense, setAddNewLicense] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<
    ILicenses | null | undefined
  >(null);

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedDeleteLicense, setSelectedDeleteLicense] = useState<
    ISelectedDeleteLicense | null | undefined
  >(null);
  const { loading, DeleteLicenseFromEntity } = DeleteBrokerageLicense();

  function changeFormat(str: string) {
    return str
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }
  const brokerageLicenseList = brokerageLicense?.map((item) => {
    return {
      id: item.node.uuid,
      licenseType: item.node.licenseType
        ? changeFormat(item.node.licenseType)
        : '—',
      licenseNumber: item.node.licenseNumber ?? '—',
      regionUuid: item.node.region?.name ?? '—',
      expirationDate: item.node?.expirationDate ?? '—',
      source: item.node.source ?? '—',
    };
  });

  const handleDelete = async () => {
    let LicenseResponseErrors;
    try {
      const { errors } = await DeleteLicenseFromEntity({
        variables: {
          entity_uuid: selectedDeleteLicense?.brokerageUuid,
          license_uuid: selectedDeleteLicense?.licenseUuid,
        },
      });
      LicenseResponseErrors = errors;

      notify(`Brokerage license has been deleted`, {
        variant: 'success',
      });
      setDeleteModal(false);
    } catch (cause) {
      console.error(`Failed to delete license. Please try again. (${cause})`);
      if (cause instanceof ApolloError) {
        LicenseResponseErrors = cause.graphQLErrors;
      }
    }
    const errorMessage = LicenseResponseErrors?.[0]?.message;
    if (errorMessage) {
      notify(
        `Failed to delete license. Please try again. (${
          errorMessage || 'No data returned by the API'
        })`,
        {
          variant: 'error',
        }
      );
      return;
    }
  };

  const noData = [
    {
      id: 0,
      licenseType: '—',
      licenseNumber: '—',
      regionUuid: '—',
      expirationDate: '—',
      source: '—',
    },
  ];

  const loadingData = Array.from({ length: 1 }, (_, index) => ({
    id: `loading-${index}`,
    licenseType: 'loading',
    licenseNumber: 'loading',
    regionUuid: 'loading',
    expirationDate: 'loading',
    source: 'loading',
  }));
  const capitalizedFirstLetter = (word: string) =>
    word.charAt(0).toUpperCase() + word.slice(1);
  const columns: GridColDef[] = [
    {
      field: 'licenseType',
      headerName: 'TYPE',
      flex: 1.0,
      sortable: true,
      renderCell: (params) =>
        params.value === 'loading'
          ? renderSkeletonCell('type')
          : renderTableCell(
              params.value,
              `type_${
                params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
              }`
            ),
    },
    {
      field: 'regionUuid',
      headerName: 'STATE',
      flex: 0.8,
      sortable: true,
      renderCell: (params) =>
        params.value === 'loading'
          ? renderSkeletonCell('state')
          : renderTableCell(
              params.value,
              `state_${
                params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
              }`
            ),
    },
    {
      field: 'licenseNumber',
      headerName: 'NUMBER',
      flex: 1,
      sortable: true,
      renderCell: (params) =>
        params.value === 'loading'
          ? renderSkeletonCell('number')
          : renderTableCell(
              params.value,
              `number_${
                params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
              }`
            ),
    },

    {
      field: 'expirationDate',
      headerName: 'EXPIRATION DATE',
      flex: 1,
      sortable: true,
      renderCell: (params) =>
        params.value === 'loading'
          ? renderSkeletonCell('expiration-date')
          : renderTableCell(
              params.value === '—' ? '—' : NewDisplayFormatDate(params.value),
              `expiration-date_${
                params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
              }`
            ),
    },
    {
      field: 'source',
      headerName: 'SOURCE',
      flex: 0.5,
      sortable: false,
      renderCell: (params) =>
        params.value === 'loading'
          ? renderSkeletonCell('source')
          : renderTableCell(
              capitalizedFirstLetter(params.value),
              `source_${
                params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
              }`
            ),
    },
    {
      field: 'iconColumn',
      headerName: '',
      flex: 0.9,
      sortable: false,
      renderCell: (params) => (
        <>
          <Typography
            className="table-cell icons"
            aria-label="info"
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '1px',
              width: '100%',
              paddingLeft: '20px',
              paddingRight: '20px',
            }}
          >
            {params.id.toString().startsWith('loading-') ? (
              <Box sx={{ display: 'flex', gap: '10px' }}>
                {/* <Skeleton animation="wave" width={20} height={20} />
                <Skeleton animation="wave" width={20} height={20} /> */}
              </Box>
            ) : brokerageLicenseList?.length ? (
              <>
                <img
                  src={config.editIcon}
                  className="edit-icon"
                  alt="edit-icon"
                  data-testid={`edit-license-button_${params.row.id}`}
                  onClick={() => {
                    const selected = brokerageLicense?.find(
                      (item) => item.node.uuid === params.id
                    );
                    setSelectedLicense(selected || null);
                    setAddNewLicense(true);
                  }}
                />
                <HighlightOffIcon
                  data-testid={`delete-license-button_${params.row.id}`}
                  style={{ marginTop: '8px' }}
                  onClick={() => {
                    setDeleteModal(true);
                    setSelectedDeleteLicense({
                      licenseUuid: params.id.toString(),
                      brokerageUuid: brokerageUuid,
                    });
                  }}
                />
              </>
            ) : (
              ''
            )}
          </Typography>
        </>
      ),
    },
  ];
  return (
    <>
      <Box
        style={{
          height:
            brokerageLicenseList && brokerageLicenseList?.length > 10
              ? '500px'
              : '',
        }}
      >
        <DataGrid
          className="license-brokerage-list"
          rowHeight={36}
          sx={{
            border: 'none',
            '& .MuiDataGrid-main': {
              borderRadius: '3px',
              border: '1px solid #cccccc',
              width: '89%',
              paddingBottom: '10px',
              height: 'auto',
              display: 'block',
              alignItems: 'center' /* Center vertically */,
              justifyContent: 'center',
              margin: 'auto',
              overflowX: 'hidden',
              overflowY: 'auto',
            },
            '& .MuiDataGrid-menuIcon': {
              display: 'none', // Hide the menu icon
            },
            '&.MuiDataGrid-cell': {
              justifyContent: 'left',
              overflow: 'hidden',
            },
            '& .MuiDataGrid-root': {
              borderColor: 'transparent',
              border: 'none', // Remove the outer border around the DataGrid
              overflow: 'hidden',
            },
            '& .MuiDataGrid-root  .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'none !important',
            },
            '& .MuiDataGrid-cell.Mui-selected': {
              backgroundColor: 'none !important',
            },
            '& .MuiDataGrid-row.Mui-selected:hover': {
              backgroundColor: 'none !important',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'none !important',
              border: 'none', // Remove the outer border around the DataGrid
            },
            '& .MuiDataGrid-cell:hover': {
              backgroundColor: 'none !important',
              border: 'none', // Remove the outer border around the DataGrid
            },
            '& .Mui-selected': {
              backgroundColor: 'inherit', // Removes the selected background color for cells
            },
            '& MuiDataGrid-virtualScrollerContent': {
              maxHeight: '36px !important',
              minHeight: '36px !important',
            },
            '& .MuiDataGrid-row': {
              backgroundColor: '#fff !important',
              borderRadius: '3px',
              fontSize: '14px',
              maxHeight: '36px !important',
              minHeight: '36px !important',
              broderTop: '1px solid #121212',
              fontWeight: 600,
              marginTop: '0p',
              textAlign: 'left',
              marginBottom: '2px !important',
              overflow: 'hidden',
              borderBottom: '1px solid rgba(224, 224, 224, 1)',
              '&:hover': {
                borderBottom: '1px solid rgba(224, 224, 224, 1) !important',
              },
            },
            '& .MuiDataGrid-cell': {
              display: 'flex',
              borderTop: 'none',
              fontWeight: '500',
              padding: '10px 0',
              alignItems: 'center',
              justifyContent: 'left',
              '& .MuiTypography-root': {
                textWrap: 'auto',
                width: '100%',
                paddingLeft: '20px',
                paddingRight: '20px',
              },
            },
            '& .MuiDataGrid-container--top [role="row"], & .MuiDataGrid-container--bottom [role="row"]':
              {
                backgroundColor: '#cefcf6',
                fontSize: '14px',
                fontWeight: 800,
                marginBottom: '0px',
              },

            '& .MuiDataGrid-columnHeader': {
              border: 'none !important',
              height: '32px !important',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
              cursor: 'default',
              outline: 'none !important',
              userSelect: 'none',
              padding: '10px 0',
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                width: '100%',
                paddingLeft: '20px',
                paddingRight: '20px',
              },
              '&:focus, &:focus-within': {
                outline: 'none !important',
              },
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              display: 'flex',
              alignItems: 'left' /* Center vertically */,
              justifyContent: 'left',
            },

            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: '600', // Make header text bold
              fontSize: '12px',
              lineHeight: '15.88px',
              letterSpacing: '0.5px',

              textTransform: 'uppercase',
            },
            '& .MuiDataGrid-virtualScroller': {
              overflowX: 'hidden',
              overflowY: 'auto',
              margin: '0px',
            },

            '& .MuiDataGrid-columnSeparator--sideRight': {
              display: 'none', // Hide the right-side border separator between header cells
            },
            '& .MuiDataGrid-footerContainer': {
              display:
                !brokerageLicenseList || brokerageLicenseList?.length <= 100
                  ? 'none'
                  : '',
            },
            '& .MuiDataGrid-selectedRowCount': {
              display: 'none', // Hide the "1 row selected" text
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none', // Remove the default outline on focus
              background: 'none',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none', // Remove the default outline when focus is within the cell
            },
          }}
          rows={
            isLoading
              ? loadingData
              : brokerageLicenseList?.length
              ? brokerageLicenseList
              : noData
          }
          columns={columns}
          onSortModelChange={(model: GridSortModel) => {
            setSortModel(model);

            if (onSortChange && model.length > 0) {
              const sortItem = model[0];
              const fieldMap: Record<string, LicenseSortKey> = {
                licenseType: LicenseSortKey.Type,
                licenseNumber: LicenseSortKey.Number,
                expirationDate: LicenseSortKey.ExpirationDate,
                source: LicenseSortKey.CreatedAt,
                regionUuid: LicenseSortKey.Region,
              };

              const sortConfig = {
                key: fieldMap[sortItem.field] || LicenseSortKey.CreatedAt,
                order:
                  sortItem.sort === 'desc' ? SortOrder.Desc : SortOrder.Asc,
              };

              // Store sort in localStorage
              setStoredSort(sortConfig);

              onSortChange(sortConfig);
            } else if (onSortChange && model.length === 0) {
              // Reset to default sort when no sort is applied
              const defaultSort = {
                key: LicenseSortKey.CreatedAt,
                order: SortOrder.Desc,
              };
              setStoredSort(defaultSort);
              onSortChange(defaultSort);
            }
          }}
          sortingOrder={['asc', 'desc', null]}
          sortModel={sortModel}
        />
      </Box>
      <Stack
        className="add-License-container"
        data-testid="add-license-button"
        onClick={() => {
          if (brokerageUuid !== undefined) {
            setAddNewLicense(true);
          } else {
            setIsBrokerageSave(true);
          }
        }}
      >
        <AddCircleOutlineIcon className="add-circle-style" /> ADD LICENSE
      </Stack>
      {isBrokerageSave && (
        <Stack>
          <LicenseModal
            title={'Save Before Adding Licenses'}
            onClose={() => {
              setIsBrokerageSave(false);
            }}
            licenseSubmitHandler={() => {
              handleSubmitFromChild();
              setIsBrokerageSave(false);
            }}
            submitLabel="Save Brokerage"
            context={
              'Please save your changes before adding licenses to this brokerage.'
            }
          />
        </Stack>
      )}
      {addNewLicense && (
        <Stack>
          <AddLicenseModal
            title={`${selectedLicense ? 'Update' : 'Add'} License`}
            selectedLicense={selectedLicense}
            setSelectedLicense={setSelectedLicense}
            onClose={() => {
              setAddNewLicense(false);
              setSelectedLicense(null);
            }}
          />
        </Stack>
      )}
      {deleteModal && (
        <Stack>
          <DeleteLicenseModal
            DeleteHandler={() => {
              handleDelete();
            }}
            title={'Delete License'}
            onClose={() => {
              setDeleteModal(false);
            }}
            submitLabel="DELETE LICENSE"
            context={
              'Are you sure you want to delete this license information?'
            }
            loading={loading}
          />
        </Stack>
      )}
    </>
  );
};

export default LicenseList;
